import React, { PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';
import { Cookies, useCookies } from 'react-cookie';

import { hasMarketingConsent as hasMarketingConsentFunction } from '../utils/consent';

type UserContextValue = {
  entryParameters: { [key: string]: string };
  userId: string | null;
  hasMarketingConsent: boolean;
};

const UserContext = React.createContext<UserContextValue | null>(null);

export { UserContext };

export const whiteList = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
  'utm_source_platform',
  'utm_creative_format',
  'utm_marketing_tactic',
  'utm_campaign_group_id',
  'utm_campaign_id',
  'utm_adgroup_id',
  'utm_ad_id',
  'utm_creative_id',
  'gbraid',
  'wbraid',
  'aff',
  'gclid',
  'gclido',
  'fbclid',
  'mdidianlass',
  'mdidiansprache',
  'pgs',
  'customerId',
  'iaid',
  'szsid',
  'tc_privacy',
  '_ga',
  'referrer',
  'slp',
  '_gl',
  'partnerId',
];

export const SESSION_KEY = 'spk.whitelist.entryparameters';

export const UserContextProvider: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  const entryParameters = useMemo<{ [key: string]: string }>(() => {
    if (typeof window === 'undefined') {
      return {};
    }
    let currentEntryParameters: { [key: string]: string } = {};
    const currentEntryparameterString = window.sessionStorage.getItem(SESSION_KEY) || '{}';
    try {
      const storedEntryParameters = JSON.parse(currentEntryparameterString);
      if (
        typeof storedEntryParameters === 'object' &&
        Object.keys(storedEntryParameters).every(
          (key) =>
            whiteList.includes(key.toLocaleLowerCase()) &&
            typeof storedEntryParameters[key] === 'string' &&
            storedEntryParameters[key].length > 0
        )
      ) {
        currentEntryParameters = storedEntryParameters;
      }
    } catch (e) {
      console.error('Could not parse entry parameters from session storage', e.message);
      currentEntryParameters = {};
    }
    const newEntryParameters = Array.from(new URLSearchParams(window.location.search).entries()).reduce(
      (parameters, [key, value]) => {
        const lowerCaseKey = key.toLocaleLowerCase();
        if (whiteList.includes(lowerCaseKey) && typeof value === 'string' && value.length > 0) {
          return { ...parameters, [lowerCaseKey]: value };
        }
        return parameters;
      },
      {}
    );
    if (Object.keys(newEntryParameters).length > 0) {
      window.sessionStorage.setItem(SESSION_KEY, JSON.stringify({ ...currentEntryParameters, ...newEntryParameters }));
    }

    return { ...currentEntryParameters, ...newEntryParameters };
  }, []);
  const [cookies] = useCookies(['_ga', 'TC_PRIVACY']);
  const [userId, setUserId] = useState<string | null>(null);
  const [hasMarketingConsent, setHasMarketingConsent] = useState<boolean | null>(null);

  useEffect(() => {
    const newContext = entryParameters;
    if (document.referrer) {
      newContext.referrer = document.referrer;
    }
    if (userId && hasMarketingConsent && Object.keys(newContext).length > 0) {
      const blob = new Blob([JSON.stringify({ context: newContext })], {
        type: 'application/json',
      });
      navigator.sendBeacon('/api/user-context/entry-parameters', blob);
    }
  }, [entryParameters, userId, hasMarketingConsent]);

  useEffect(() => {
    if (cookies['TC_PRIVACY'] && cookies['_ga']) {
      setHasMarketingConsent(hasMarketingConsentFunction(cookies['TC_PRIVACY']));
      setUserId(cookies['_ga']);
    }
    let interval = null;
    if (!cookies['TC_PRIVACY']) {
      interval = setInterval(() => {
        const cookieReader = new Cookies();
        const tcPrivacy = cookieReader.get('TC_PRIVACY');
        const ga = cookieReader.get('_ga');
        if (tcPrivacy && ga) {
          setHasMarketingConsent(hasMarketingConsentFunction(tcPrivacy));
          setUserId(ga);
          clearInterval(interval);
        }
      }, 500);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [cookies]);

  return (
    <UserContext.Provider value={{ entryParameters, hasMarketingConsent, userId }}>{children}</UserContext.Provider>
  );
};

export const useUserContext = (): UserContextValue | null => {
  return useContext(UserContext);
};
